/**
 * melysium custom cart drawer additions
 *
 * Extends Dawn's native cart-drawer (cart-drawer.js / cart.js) instead of
 * replacing it — quantity changes, remove, and focus handling all keep
 * working exactly as before. This file only adds:
 *   1. Upsell "Hinzufügen" buttons (POST /cart/add.js, then re-render via
 *      the existing <cart-drawer> element's renderContents()).
 *   2. Discount code apply / remove (POST /cart/update.js with a
 *      comma-separated discount list, then refresh the cart-drawer section).
 *   3. Upsell carousel paging (groups of 3).
 *   4. Minus-Button auf 0 -> Artikel komplett entfernen.
 */
(function () {
  const UPSELL_PAGE_SIZE = 3;

  // ---- iOS: Drawer an den sichtbaren Viewport koppeln (Höhe UND Position) ----
  // Öffnet sich die Tastatur (z.B. beim Rabattcode-Feld), passieren auf iOS
  // ZWEI Dinge:
  //   1. Der sichtbare Bereich schrumpft (visualViewport.height).
  //   2. Safari verschiebt den sichtbaren Ausschnitt nach oben/unten, um das
  //      fokussierte Feld ins Bild zu holen (visualViewport.offsetTop) —
  //      position:fixed-Elemente wie der Drawer bleiben dabei am LAYOUT-
  //      Viewport kleben und rutschen visuell aus dem Bild (Header weg,
  //      Hintergrund sichtbar, "komisches Rumscrollen").
  // Fix: Höhe UND vertikalen Offset live per CSS-Variablen nachführen, damit
  // der Drawer immer exakt den sichtbaren Bereich füllt — Header oben,
  // Rabattcode/Footer unten, egal ob Tastatur offen ist oder nicht.
  function updateDrawerViewport() {
    const vv = window.visualViewport;
    const vh = (vv && vv.height) || window.innerHeight;
    const offset = vv ? vv.offsetTop : 0;
    document.documentElement.style.setProperty('--mlys-drawer-vh', `${vh}px`);
    document.documentElement.style.setProperty('--mlys-drawer-offset', `${offset}px`);
  }
  updateDrawerViewport();
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateDrawerViewport);
    window.visualViewport.addEventListener('scroll', updateDrawerViewport);
  } else {
    window.addEventListener('resize', updateDrawerViewport);
  }

  function getCartDrawerElement() {
    return document.querySelector('cart-drawer');
  }

  function getAppliedCodes() {
    return Array.from(document.querySelectorAll('.mlys-remove-code')).map((btn) => btn.dataset.code);
  }

  function postDiscountCodes(codes) {
    const body = JSON.stringify({ discount: codes.join(',') });
    return fetch(window.routes.cart_update_url, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
  }

  function refreshCartDrawerSection() {
    return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = doc.querySelector('#CartDrawer');
        const currentDrawer = document.querySelector('#CartDrawer');
        if (freshDrawer && currentDrawer) {
          currentDrawer.replaceWith(freshDrawer);
        }
      });
  }

  function initUpsellCarousels(scope) {
    scope.querySelectorAll('.mlys-upsell-grid').forEach((grid) => {
      if (!grid.dataset.mlysPage) grid.dataset.mlysPage = '0';
      paginateUpsellGrid(grid);
    });
  }

  function paginateUpsellGrid(grid) {
    const cards = Array.from(grid.querySelectorAll('.mlys-upsell-card'));
    const page = parseInt(grid.dataset.mlysPage || '0', 10);
    const maxPage = Math.max(0, Math.ceil(cards.length / UPSELL_PAGE_SIZE) - 1);
    const clampedPage = Math.min(page, maxPage);
    grid.dataset.mlysPage = String(clampedPage);

    cards.forEach((card, index) => {
      const inRange = index >= clampedPage * UPSELL_PAGE_SIZE && index < (clampedPage + 1) * UPSELL_PAGE_SIZE;
      card.classList.toggle('mlys-visible', inRange);
    });

    const carousel = grid.closest('.mlys-upsell-carousel');
    if (!carousel) return;
    const prevBtn = carousel.querySelector('.mlys-carousel-prev');
    const nextBtn = carousel.querySelector('.mlys-carousel-next');
    if (prevBtn) prevBtn.disabled = clampedPage === 0;
    if (nextBtn) nextBtn.disabled = clampedPage >= maxPage;
  }

  document.addEventListener('DOMContentLoaded', () => initUpsellCarousels(document));

  // Re-init pagination whenever the cart-drawer content is swapped in
  // (add-to-cart, discount apply/remove, quantity change all replace #CartDrawer).
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && document.querySelector('.mlys-upsell-grid')) {
        initUpsellCarousels(document);
        break;
      }
    }
  });
  document.addEventListener('DOMContentLoaded', () => {
    const target = document.body;
    if (target) observer.observe(target, { childList: true, subtree: true });
  });

  document.body.addEventListener('click', (event) => {
    // ---- Upsell: vorherige/nächste Seite ----
    const prevBtn = event.target.closest('.mlys-carousel-prev');
    if (prevBtn) {
      const grid = prevBtn.closest('.mlys-upsell-carousel').querySelector('.mlys-upsell-grid');
      const page = parseInt(grid.dataset.mlysPage || '0', 10);
      grid.dataset.mlysPage = String(Math.max(0, page - 1));
      paginateUpsellGrid(grid);
      return;
    }
    const nextBtn = event.target.closest('.mlys-carousel-next');
    if (nextBtn) {
      const grid = nextBtn.closest('.mlys-upsell-carousel').querySelector('.mlys-upsell-grid');
      const page = parseInt(grid.dataset.mlysPage || '0', 10);
      grid.dataset.mlysPage = String(page + 1);
      paginateUpsellGrid(grid);
      return;
    }

    // ---- Upsell: hinzufügen (mit kurzer Häkchen-Bestätigung) ----
    const addBtn = event.target.closest('.mlys-upsell-add');
    if (addBtn && !addBtn.disabled) {
      // MELYSIUM CUSTOM: Falls die Karte ein Varianten-Dropdown hat (z.B.
      // Duft), nehmen wir dessen aktuell gewählten Wert statt der
      // statischen data-variant-id vom Button.
      const card = addBtn.closest('.mlys-upsell-card');
      const variantSelect = card && card.querySelector('.mlys-upsell-variant');
      const variantId = variantSelect ? variantSelect.value : addBtn.dataset.variantId;
      if (!variantId) return;

      addBtn.disabled = true;
      addBtn.classList.add('mlys-confirmed');
      addBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg> Hinzugefügt';

      setTimeout(() => {
        const cartDrawerEl = getCartDrawerElement();
        const sections =
          cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function'
            ? cartDrawerEl.getSectionsToRender().map((section) => section.id)
            : ['cart-drawer', 'cart-icon-bubble'];

        fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              addBtn.disabled = false;
              addBtn.classList.remove('mlys-confirmed');
              addBtn.textContent = 'Hinzufügen';
              return;
            }
            if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
              cartDrawerEl.renderContents(parsedState);
            }
          })
          .catch(() => {
            addBtn.disabled = false;
            addBtn.classList.remove('mlys-confirmed');
            addBtn.textContent = 'Hinzufügen';
          });
      }, 650);
      return;
    }

    // ---- Rabattcode anwenden ----
    const applyBtn = event.target.closest('.mlys-promo-apply');
    if (applyBtn) {
      const input = document.getElementById('mlys-promo-input');
      const errorEl = document.getElementById('mlys-promo-error');
      if (!input) return;
      const newCode = input.value.trim();
      if (!newCode) return;

      const existingCodes = getAppliedCodes();
      if (existingCodes.some((code) => code.toUpperCase() === newCode.toUpperCase())) {
        if (errorEl) {
          errorEl.textContent = 'Code ist schon angewendet.';
          errorEl.hidden = false;
        }
        return;
      }

      applyBtn.disabled = true;
      const nextCodes = existingCodes.concat([newCode]);

      postDiscountCodes(nextCodes)
        .then(() => refreshCartDrawerSection())
        .then(() => {
          const stillApplied = getAppliedCodes();
          const freshError = document.getElementById('mlys-promo-error');
          const matched = stillApplied.some((code) => code.toUpperCase() === newCode.toUpperCase());
          if (freshError) {
            if (matched) {
              freshError.hidden = true;
            } else {
              freshError.textContent = 'Code ungültig, bitte prüfen.';
              freshError.hidden = false;
              // 29.10.2026: Codefeld ist eingeklappt (<details>), nach dem
              // Neu-Rendern wieder aufklappen, damit der Fehler sichtbar ist.
              const freshDetails = freshError.closest('details');
              if (freshDetails) freshDetails.open = true;
            }
          }
        })
        .catch(() => {
          const freshError = document.getElementById('mlys-promo-error');
          if (freshError) {
            freshError.textContent = 'Da ist etwas schiefgelaufen, bitte nochmal versuchen.';
            freshError.hidden = false;
            const freshDetails = freshError.closest('details');
            if (freshDetails) freshDetails.open = true;
          }
          const freshApplyBtn = document.querySelector('.mlys-promo-apply');
          if (freshApplyBtn) freshApplyBtn.disabled = false;
        });
      return;
    }

    // ---- Rabattcode entfernen ----
    const removeBtn = event.target.closest('.mlys-remove-code');
    if (removeBtn) {
      const codeToRemove = removeBtn.dataset.code;
      const remainingCodes = getAppliedCodes().filter((code) => code !== codeToRemove);
      postDiscountCodes(remainingCodes).then(() => refreshCartDrawerSection());
      return;
    }
  });

  // ---- Minus-Button auf 0 -> Artikel komplett entfernen ----
  // Dawn zeigt normalerweise nur einen "Minimum von 1"-Hinweis und lässt die
  // Menge auf 0 im Feld stehen (kaputter Zustand). Wir fangen den Klick im
  // Capture-Modus ab (läuft VOR Dawns eigenem Bubble-Listener, DOM-Wert ist
  // an dieser Stelle noch unverändert), und lösen bei aktuellem Wert 1
  // stattdessen den vorhandenen, bereits funktionierenden Entfernen-Button
  // aus — keine eigene Entfernen-Logik nötig.
  document.addEventListener(
    'click',
    (event) => {
      const minusBtn = event.target.closest('.cart-drawer .quantity__button[name="minus"]');
      if (!minusBtn) return;

      const wrapper = minusBtn.closest('.cart-item__quantity-wrapper');
      const input = wrapper && wrapper.querySelector('.quantity__input');
      if (!input || parseInt(input.value, 10) !== 1) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();

      const removeBtn = wrapper.querySelector('cart-remove-button button');
      if (removeBtn) removeBtn.click();
    },
    true
  );

  document.body.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.id === 'mlys-promo-input') {
      event.preventDefault();
      const applyBtn = document.querySelector('.mlys-promo-apply');
      if (applyBtn) applyBtn.click();
    }
  });

  // ---- iOS Safari: Hintergrund scrollt durch beim Wischen auf nicht-
  // scrollbaren Bereichen des offenen Drawers ----
  // Dawn sperrt den Hintergrund beim Öffnen nur mit "overflow: hidden" auf
  // dem <body>. Das reicht auf iOS Safari nicht aus, um Touch-Wisch-Gesten
  // auf fixed-position-Elementen zu blockieren — wischt man auf dem
  // Versand-Banner oben oder dem Footer (Rabattcode/Gesamtbetrag) statt auf
  // der eigentlichen Artikel-Liste, scrollt iOS stattdessen die Seite
  // dahinter. Fix: touchmove außerhalb der scrollbaren Artikel-Liste
  // (#CartDrawer-CartItems) unterdrücken, solange der Drawer offen ist.
  document.addEventListener(
    'touchmove',
    (event) => {
      const cartDrawerEl = getCartDrawerElement();
      if (!cartDrawerEl || !cartDrawerEl.classList.contains('active')) return;
      if (event.target.closest('#CartDrawer-CartItems')) return;
      event.preventDefault();
    },
    { passive: false }
  );
})();
